import React, { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client';
import { Alert, Modal } from 'antd';
// Api
import { SET_CAMPAIGN_AGREEMENT_SIGNATORY } from 'api/campaignV2/mutations';
// Types
import {
  SetCampaignAgreementSignatory,
  SetCampaignAgreementSignatoryVariables,
} from 'api/campaignV2/types/SetCampaignAgreementSignatory';
// Ui
import InfoTip from 'ui/InfoTip/InfoTip';
import { errorNotification } from 'ui/Notification';
// Components
import AgreementSignatorySelect, {
  SIGNATORY_TIP,
} from 'components/common/AgreementSignatorySelect/AgreementSignatorySelect';
// Styles
import styles from './ApproveWithSignatoryModal.module.scss';

type ApproveWithSignatoryModalProps = {
  visible: boolean;
  campaignId: string;
  ownerId?: string | null;
  /** Approval in progress, from the caller's approve mutation */
  approving?: boolean;
  onCancel: () => void;
  /** Runs once the signatory is saved */
  onApprove: () => Promise<void>;
};

/**
 * A campaign that requires the digital agreement can't be approved without a brand signatory. When no brand user
 * launched it, the admin chooses one here and the campaign is approved right after.
 */
const ApproveWithSignatoryModal = ({
  visible,
  campaignId,
  ownerId,
  approving = false,
  onCancel,
  onApprove,
}: ApproveWithSignatoryModalProps): JSX.Element => {
  const [signatoryId, setSignatoryId] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setSignatoryId(null);
    }
  }, [visible]);

  const [setSignatory, { loading: saving }] = useMutation<
    SetCampaignAgreementSignatory,
    SetCampaignAgreementSignatoryVariables
  >(SET_CAMPAIGN_AGREEMENT_SIGNATORY);

  const handleOk = async () => {
    if (!signatoryId) {
      return;
    }

    try {
      await setSignatory({
        variables: { input: { campaignId, agreementSignatoryId: signatoryId } },
      });
    } catch (error: any) {
      console.error('Error setting agreement signatory', error);
      errorNotification(error?.message || 'Error setting agreement signatory');
      return;
    }

    await onApprove();
  };

  return (
    <Modal
      title="Choose the agreement signatory"
      visible={visible}
      onOk={handleOk}
      onCancel={onCancel}
      okText="Set signatory and approve"
      okButtonProps={{ disabled: !signatoryId }}
      confirmLoading={saving || approving}
      destroyOnClose
    >
      <Alert
        className={styles.alert}
        type="info"
        showIcon
        message="This campaign requires the digital agreement"
        description="Talents sign an agreement before they apply, and a brand user countersigns it. No brand user launched this campaign, so nobody is set to sign for the brand yet. Choose who signs before approving."
      />

      <p className={styles.label}>
        Agreement signatory
        <InfoTip title={SIGNATORY_TIP} />
      </p>
      <AgreementSignatorySelect
        campaignId={campaignId}
        ownerId={ownerId}
        value={signatoryId}
        onChange={setSignatoryId}
      />
    </Modal>
  );
};

export default ApproveWithSignatoryModal;
